import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  ArrowLeft,
  CalendarCheck2,
  MessageCircle,
  Sparkles,
  Check,
  ChevronDown,
  Info,
  Leaf,
} from "lucide-react";
import {
  NUTRIZIONE_INFO,
  NUTRIZIONE_PACKAGES,
  NUTRIZIONE_TESTIMONIALS,
  CONTACT,
} from "@/data/siteData";
import SEO from "@/components/SEO";
import { trackBookingClick, trackWhatsappClick } from "@/lib/analytics";
import { Star } from "lucide-react";

const PRINCIPI = [
  {
    icon: "Scissors",
    title: "Sartoriale",
    desc: "Ogni dieta è cucita addosso al paziente: patologia, abitudini, vita quotidiana e gusti.",
  },
  {
    icon: "BookOpenCheck",
    title: "Basata sull'evidenza",
    desc: "Protocolli scientificamente validati — dieta mediterranea, low-FODMAP, mTOR, approcci clinici.",
  },
  {
    icon: "HeartPulse",
    title: "Sostenibile",
    desc: "Niente diete estreme. Percorsi che si integrano nella vita reale e si mantengono nel tempo.",
  },
];

function PackageCard({ pkg, index }) {
  const [open, setOpen] = useState(index === 0 || pkg.featured);
  const Icon = Icons[pkg.icon] || Icons.Leaf;
  const wa = `https://wa.me/39${CONTACT.whatsapp}`;

  return (
    <div
      data-testid={`nutri-pkg-${pkg.slug}`}
      className={`relative rounded-2xl overflow-hidden transition-all duration-500 ${
        pkg.featured
          ? "bg-[#2F4A3C] text-white shadow-[0_18px_50px_rgba(47,74,60,0.25)]"
          : "bg-white border border-[#E8E2D1] shadow-[0_6px_24px_rgba(47,74,60,0.06)]"
      }`}
    >
      {pkg.featured && (
        <span className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-medium tracking-[0.2em] uppercase text-[#C6A86B]">
          <Sparkles size={11} strokeWidth={2} /> Consigliato
        </span>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`nutri-pkg-toggle-${pkg.slug}`}
        className="w-full text-left p-7 md:p-8 flex items-start gap-5"
      >
        <div
          className={`w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center transition-colors ${
            pkg.featured
              ? "bg-white/10 text-[#C6A86B]"
              : "bg-[#F2EBD9] text-[#2F4A3C]"
          }`}
        >
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p
                className={`text-[10px] uppercase tracking-[0.22em] font-medium ${
                  pkg.featured ? "text-[#C6A86B]" : "text-[#8E7E52]"
                }`}
              >
                {pkg.tier}
              </p>
              <h3
                className={`font-serif text-xl md:text-2xl mt-1 leading-tight ${
                  pkg.featured ? "text-white" : "text-[#2F4A3C]"
                }`}
              >
                {pkg.name}
              </h3>
              <p
                className={`mt-2 text-[14px] leading-relaxed ${
                  pkg.featured ? "text-white/70" : "text-gray-500"
                }`}
              >
                {pkg.tagline}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="flex items-baseline gap-1 justify-end">
                <span
                  className={`text-xs ${pkg.featured ? "text-white/60" : "text-gray-400"}`}
                >
                  da
                </span>
                <span
                  className={`font-serif text-3xl md:text-4xl ${
                    pkg.featured ? "text-white" : "text-[#2F4A3C]"
                  }`}
                >
                  €{pkg.price}
                </span>
              </div>
              <ChevronDown
                size={18}
                strokeWidth={1.6}
                className={`inline-block mt-2 transition-transform duration-300 ${
                  open ? "rotate-180" : ""
                } ${pkg.featured ? "text-white/60" : "text-gray-400"}`}
              />
            </div>
          </div>
        </div>
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          open ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-7 md:px-8 pb-7 md:pb-8 pl-[88px] md:pl-[108px]">
          <div
            className={`h-px w-16 mb-6 ${
              pkg.featured ? "bg-[#C6A86B]/40" : "bg-[#C6A86B]/60"
            }`}
          />
          <p
            className={`text-[11px] uppercase tracking-[0.18em] font-medium mb-4 ${
              pkg.featured ? "text-[#C6A86B]" : "text-[#8E7E52]"
            }`}
          >
            Cosa include
          </p>
          <ul className="space-y-3">
            {pkg.features.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <span
                  className={`mt-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                    pkg.featured
                      ? "bg-[#C6A86B]/20 text-[#C6A86B]"
                      : "bg-[#C6A86B]/15 text-[#C6A86B]"
                  }`}
                >
                  <Check size={10} strokeWidth={2.4} />
                </span>
                <span
                  className={`text-[14px] md:text-[15px] leading-relaxed ${
                    pkg.featured ? "text-white/85" : "text-gray-700"
                  }`}
                >
                  {f}
                </span>
              </li>
            ))}
          </ul>

          {pkg.patologie && (
            <p
              className={`mt-6 text-[13px] leading-relaxed ${
                pkg.featured ? "text-white/60" : "text-gray-500"
              }`}
            >
              <span className="font-medium">Indicazioni cliniche: </span>
              {pkg.patologie}.
            </p>
          )}

          {pkg.note && (
            <div
              className={`mt-5 flex items-start gap-2 text-[13px] italic ${
                pkg.featured ? "text-[#C6A86B]/80" : "text-[#8E7E52]"
              }`}
            >
              <Info size={13} strokeWidth={1.8} className="mt-0.5 flex-shrink-0" />
              <span>{pkg.note}</span>
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={CONTACT.miodottore}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookingClick(`nutrizione_${pkg.slug}`)}
              data-testid={`nutri-pkg-book-${pkg.slug}`}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                pkg.featured
                  ? "bg-[#C6A86B] text-[#2F4A3C] hover:bg-[#D4B97F]"
                  : "bg-[#2F4A3C] text-white hover:bg-[#3B5B4A]"
              }`}
            >
              <CalendarCheck2 size={16} strokeWidth={1.6} />
              Prenota
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsappClick(`nutrizione_${pkg.slug}`)}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium border transition-all ${
                pkg.featured
                  ? "border-white/25 text-white hover:border-white/50"
                  : "border-[#2F4A3C]/20 text-[#2F4A3C] hover:border-[#2F4A3C]/50"
              }`}
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              Info WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Nutrizione() {
  const wa = `https://wa.me/39${CONTACT.whatsapp}`;
  return (
    <main
      data-testid="nutrizione-page"
      className="pt-28"
      style={{ backgroundColor: "#FAF6EF" }}
    >
      <SEO
        title="Nutrizione clinica · Diete sartoriali"
        description="Nutrizione clinica a Cagliari e Portoscuso. Diete personalizzate per colon irritabile (FODMAP), celiachia, diabete, dislipidemie, pancreatite, sindrome metabolica. Visite medico-nutrizionali con il Dr. Cristian Santus."
        path="/nutrizione"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={NUTRIZIONE_INFO.heroImage}
            alt="Nutrizione clinica — Dr. Cristian Santus"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 60%" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(100deg, rgba(47,74,60,0.95) 0%, rgba(47,74,60,0.82) 45%, rgba(47,74,60,0.35) 85%, rgba(47,74,60,0.2) 100%)",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 min-h-[70vh] md:min-h-[72vh] flex items-center py-20 md:py-24">
          <div className="max-w-2xl">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={14} /> Torna al sito principale
            </Link>

            <div className="flex items-center gap-2 mt-10">
              <span className="inline-flex items-center gap-1.5 bg-[#C6A86B] text-[#2F4A3C] text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
                <Sparkles size={11} strokeWidth={2.2} /> Novità ·{" "}
                {NUTRIZIONE_INFO.startDate}
              </span>
            </div>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 text-white leading-[1.02]">
              Nutrizione clinica
            </h1>
            <p className="mt-6 text-xl md:text-2xl font-serif italic text-[#C6A86B]">
              Diete sartoriali, cucite addosso al paziente.
            </p>
            <div
              className="mt-8 h-px w-24"
              style={{ background: "rgba(198,168,107,0.6)" }}
            />
            <p className="mt-8 text-lg text-white/80 leading-relaxed">
              {NUTRIZIONE_INFO.intro}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#pacchetti"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("pacchetti")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="nutri-hero-cta-pacchetti"
                className="inline-flex items-center gap-2 bg-[#C6A86B] text-[#2F4A3C] px-7 py-4 rounded-full text-sm font-semibold hover:bg-[#D4B97F] hover:-translate-y-0.5 transition-all shadow-[0_10px_30px_rgba(198,168,107,0.3)]"
              >
                <Leaf size={16} strokeWidth={1.8} />
                Scopri i pacchetti
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsappClick("nutrizione_hero")}
                className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-4 rounded-full text-sm font-medium hover:border-white/60 transition-all"
              >
                <MessageCircle size={16} strokeWidth={1.6} />
                Contattami su WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Principi */}
      <section className="relative py-24 md:py-28" style={{ backgroundColor: "#FAF6EF" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C6A86B] font-medium">
              Il mio approccio
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#2F4A3C] tracking-tight leading-tight">
              Non c'è una dieta uguale all'altra.
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed text-[17px]">
              Ogni paziente è una storia unica. La dieta che prescrivo nasce
              dall'ascolto, dalla diagnosi clinica e dalla conoscenza delle
              abitudini reali di chi ho davanti.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPI.map((p) => {
              const Ic = Icons[p.icon] || Icons.Leaf;
              return (
                <div
                  key={p.title}
                  className="bg-white rounded-2xl p-8 border border-[#E8E2D1] shadow-[0_6px_24px_rgba(47,74,60,0.04)] hover:shadow-[0_12px_40px_rgba(47,74,60,0.08)] hover:border-[#C6A86B]/40 transition-all duration-500 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F2EBD9] text-[#2F4A3C] flex items-center justify-center group-hover:bg-[#C6A86B]/20 group-hover:scale-105 transition-all">
                    <Ic size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl mt-5 text-[#2F4A3C]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pacchetti */}
      <section
        id="pacchetti"
        className="relative py-24 md:py-32"
        style={{ backgroundColor: "#F2EBD9" }}
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C6A86B] font-medium">
              Pacchetti
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#2F4A3C] tracking-tight leading-tight">
              Scegli il tuo percorso.
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed">
              Clicca su ciascun pacchetto per vedere nel dettaglio cosa include.
              Tutti i prezzi sono "a partire da" e si riferiscono al percorso
              completo.
            </p>
          </div>

          <div className="mt-14 space-y-5">
            {NUTRIZIONE_PACKAGES.map((p, i) => (
              <PackageCard key={p.slug} pkg={p} index={i} />
            ))}
          </div>

          <div className="mt-10 flex items-start gap-3 text-sm text-gray-600 max-w-3xl">
            <Info size={16} className="text-[#C6A86B] mt-0.5 flex-shrink-0" strokeWidth={1.6} />
            <p className="leading-relaxed">
              Ai prezzi indicati va aggiunta una <strong>marca da bollo di €2</strong>{" "}
              per ogni fattura, come previsto dalla normativa vigente.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonianze */}
      <section
        className="relative py-24 md:py-32"
        style={{ backgroundColor: "#FAF6EF" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C6A86B] font-medium">
              Testimonianze
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#2F4A3C] tracking-tight leading-tight">
              Cosa dicono i miei pazienti.
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed">
              Storie reali di chi ha intrapreso un percorso nutrizionale e ha
              ritrovato equilibrio, energia e serenità a tavola.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {NUTRIZIONE_TESTIMONIALS.map((t, i) => (
              <blockquote
                key={i}
                data-testid={`nutri-review-${i}`}
                className="bg-white rounded-2xl p-8 border border-[#E8E2D1] shadow-[0_6px_24px_rgba(47,74,60,0.04)] hover:shadow-[0_12px_40px_rgba(47,74,60,0.08)] hover:border-[#C6A86B]/40 transition-all duration-500"
              >
                <div className="flex gap-0.5 mb-5">
                  {[0, 1, 2, 3, 4].map((k) => (
                    <Star
                      key={k}
                      size={16}
                      className="text-[#C6A86B]"
                      fill="#C6A86B"
                      strokeWidth={0}
                    />
                  ))}
                </div>
                <p className="font-serif text-lg md:text-xl leading-[1.4] text-[#2F4A3C]">
                  “{t.text}”
                </p>
                <footer className="mt-6 text-sm text-gray-500">
                  <span className="text-[#2F4A3C] font-medium">{t.author}</span>
                  <span className="mx-2">·</span>
                  {t.city}
                </footer>
              </blockquote>
            ))}
          </div>

          <p className="mt-10 text-[11px] text-gray-400 italic leading-relaxed max-w-3xl">
            Testimonianze rappresentative di casi reali. I nomi e alcuni
            dettagli sono stati modificati per tutela della privacy dei
            pazienti, nel rispetto del Regolamento GDPR (UE 2016/679).
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative bg-[#2F4A3C] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${NUTRIZIONE_INFO.secondaryImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(47,74,60,0.92) 0%, rgba(47,74,60,0.98) 100%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C6A86B] font-medium">
            Inizia il tuo percorso
          </p>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 text-white leading-tight">
            Vuoi un consiglio prima di scegliere?
          </h2>
          <p className="mt-5 text-white/75 text-lg max-w-xl mx-auto">
            Scrivimi su WhatsApp o compila il modulo di contatto: ti aiuterò a
            individuare il pacchetto più adatto alla tua situazione clinica.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <a
              href={CONTACT.miodottore}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookingClick("nutrizione_final")}
              className="inline-flex items-center gap-2 bg-[#C6A86B] text-[#2F4A3C] px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#D4B97F] transition-all hover:-translate-y-0.5"
            >
              <CalendarCheck2 size={16} strokeWidth={1.8} />
              Prenota una visita
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsappClick("nutrizione_final")}
              className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-sm font-medium hover:border-[#C6A86B] hover:text-[#C6A86B] transition-all"
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              Chiedi un consiglio
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
